import { isServerSide } from '../../utils/server';

import { GooglePlacesAutocompleteType } from './types';

let AutocompleteService: google.maps.places.AutocompleteService | null = null;

export const getItemValue = (item: google.maps.places.AutocompletePrediction | null) =>
  item?.description ?? '';

export const getNewSessionToken = (): google.maps.places.AutocompleteSessionToken | null => {
  if (!isServerSide() && window.google != null) {
    return new google.maps.places.AutocompleteSessionToken();
  }
  return null;
};

export const getPlacePredictions = (
  input: string,
  type: GooglePlacesAutocompleteType = GooglePlacesAutocompleteType.postalCode,
  sessionToken: google.maps.places.AutocompleteSessionToken | null = null,
) => {
  if (!isServerSide() && AutocompleteService == null && window.google != null) {
    AutocompleteService = new google.maps.places.AutocompleteService();
  }

  return new Promise<google.maps.places.AutocompletePrediction[] | null>((resolve, reject) => {
    if (AutocompleteService == null) {
      reject(new Error('AutocompleteService is not defined'));
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises  -- auto-ignored when updating eslint
    AutocompleteService?.getPlacePredictions(
      {
        componentRestrictions: { country: ['fr'] },
        types: [type],
        input,
        sessionToken: sessionToken ?? undefined,
      },
      (
        result: google.maps.places.AutocompletePrediction[] | null,
        status: google.maps.places.PlacesServiceStatus,
      ) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access  -- auto-ignored when updating eslint
        if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          resolve([]);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access  -- auto-ignored when updating eslint
        } else if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
          reject(new Error(status));
        } else {
          resolve(result);
        }
      },
    );
  });
};

export const highlightMatch = (
  text: string,
  substrings: google.maps.places.PredictionSubstring[],
  highlightTag = 'span',
  highlightClass = 'highlight',
) => {
  const substringsToReplace = substrings.map(
    ({ offset, length }: google.maps.places.PredictionSubstring) =>
      text.substring(offset, offset + length),
  );

  return substringsToReplace.reduceRight(
    (acc: string, stringToReplace: string) =>
      acc.replace(
        stringToReplace,
        `<${highlightTag} class='${highlightClass}'>${stringToReplace}</${highlightTag}>`,
      ),
    text,
  );
};

export const hasStreetNumber = (predictions: google.maps.places.AutocompletePrediction[]) => {
  return predictions.some(prediction => {
    if (prediction.terms.length === 0) return false;
    const firstTerm = prediction.terms[0];

    if (
      firstTerm.offset === 0 &&
      !Number.isNaN(Number.parseInt(firstTerm.value, 10)) // Will loosely convert to number if possible, and then check if it is a valid number
    ) {
      return true;
    }

    return false;
  });
};

const PREDICTION_TYPES = ['street_address', 'premise', 'establishment'];

// The street_number address component may not be present in Place Details responses for place IDs in some autocomplete predictions.
// This typically happens when the address cannot be interpolated for a place. In these cases, the prediction's types array will contain
// "route". For autocomplete predictions with a number in the description (input query is matched), the Place Details response for the place
// ID contains the street number only if the types array for the prediction contains at least one of:
// street_address
// premise
// establishment
// Source: https://developers.google.com/maps/documentation/places/web-service/autocomplete#PlacesAutocompleteResponse
export const filterPredictions = (predictions: google.maps.places.AutocompletePrediction[]) =>
  predictions.filter(prediction => prediction.types.some(item => PREDICTION_TYPES.includes(item)));
